/**
 * Lightweight unread notification count for the header badge.
 * Reuses dashboard batch cache when available; otherwise fetches count only.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { notificationApiService } from "@/services/notificationApiService";
import {
  getDashboardUnreadCount,
  isDashboardBatchFetching,
  shouldDeferToDashboardBatch,
} from "@/utils/dashboardQueryCache";
import { useNotificationRealtime } from "@/hooks/useNotificationRealtime";

/**
 * Use Header Unread Count.
 */
export function useHeaderUnreadCount(): number {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const cachedCount = getDashboardUnreadCount(queryClient, user?.id);

  useNotificationRealtime(user?.id);
  const dashboardBatchFetching = isDashboardBatchFetching(queryClient, user?.id);
  const deferToDashboard = shouldDeferToDashboardBatch(queryClient, user?.id);

  const { data: fetchedCount = 0 } = useQuery({
    queryKey: ["notifications-unread-count", user?.id],
    queryFn: async (): Promise<number> => {
      if (!user?.id) {
        return 0;
      }
      const count = await notificationApiService.fetchUnreadCount(user.id);
      return count;
    },
    enabled:
      !!user?.id && cachedCount === undefined && !dashboardBatchFetching && !deferToDashboard,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return cachedCount ?? fetchedCount;
}
